import React, {
	Dispatch,
	SetStateAction,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";
import classNames from "classnames";
import { DeleteOutlineRounded } from "@mui/icons-material";

import { useGetCourseFilesQuery } from "@/pages/Private/redux/courses/courses.api";

import avatars from "../../Courses/components/Editor/avatars";
import { FileAssignment } from "../../Courses/enums/FileAssignment.enum";

interface RadioGroupProps {
	onChange: (value: string | { id: string; url: string }) => void;
	label: string;
	disabled?: boolean;
	removeAvatarFile: (url: string) => Promise<void>;
	setUploadedAvatars: Dispatch<SetStateAction<{ id: string; url: string }[]>>;
	selectedAvatarId: string;
	setSelectedAvatarId: Dispatch<SetStateAction<string>>;
	courseId: number;
	uploadedAvatars: { id: string; url?: string }[];
}

export const AvatarButtons: React.FC<RadioGroupProps> = ({
	label,
	removeAvatarFile,
	onChange,
	setUploadedAvatars,
	setSelectedAvatarId,
	disabled,
	selectedAvatarId,
	courseId,
	uploadedAvatars,
}) => {
	const [activeId, setActiveId] = useState(selectedAvatarId);
	const { data: fetchedCourseFiles } = useGetCourseFilesQuery(+courseId, {
		refetchOnMountOrArgChange: true, // ✅ forces refetch every mount / arg change
	});

	const handleSelect = (value: string | { id: string; url: string }) => {
		if (typeof value === "string" && value === "0") {
			setActiveId("");
			onChange("");
		} else if (typeof value === "string") {
			setActiveId(value);
			onChange(value);
		} else {
			setActiveId(value.id);
			onChange({ id: value.id, url: value.url });
		}
	};

	const avatarFetchedCourseFiles = useMemo(() => {
		return (
			fetchedCourseFiles
				?.filter((file) => file.fileAssignment === FileAssignment.Avatar)
				.map((file) => ({
					id: String(file.id),
					url: file.fullFilePath,
				})) ?? []
		);
	}, [fetchedCourseFiles]);

	useLayoutEffect(() => {
		setSelectedAvatarId(selectedAvatarId);
		setUploadedAvatars((curAvatars) => {
			const newAvatars = avatarFetchedCourseFiles.filter(
				(fetched) => !curAvatars.some((current) => current.id === fetched.id)
			);

			if (newAvatars.length > 0) {
				return [...avatars, ...newAvatars];
			}

			return [...avatars, ...avatarFetchedCourseFiles];
		});
	}, [avatarFetchedCourseFiles]);

	useEffect(() => {
		if (selectedAvatarId && selectedAvatarId !== activeId) {
			setActiveId(selectedAvatarId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAvatarId]);

	return (
		<div className="w-full">
			{label && (
				<label className="block mt-1 text-[0.875rem] font-bold text-primary-gray appearance-none">
					{label}
				</label>
			)}
			<div className="grid grid-cols-3 gap-4">
				{uploadedAvatars.map((item) => {
					return (
						<div key={item.id} className="relative ">
							<button
								className={classNames(
									!Object.hasOwn(item, "url") && "input-styles", // use the modified class without focus border
									!Object.hasOwn(item, "url") &&
										(activeId === item.id
											? "bg-primary-blue text-white border-transparent hover:bg-primary-blue-hover"
											: "hover:text-white hover:bg-primary-blue border border-gray-200"),
									Object.hasOwn(item, "url") &&
										"w-[80px] h-[80px] box-border bg-cover bg-center bg-no-repeat rounded-xl border-2",
									Object.hasOwn(item, "url") &&
										(activeId === item.id ? "border-blue-action border-2" : "border-transparent")
								)}
								disabled={disabled}
								style={item.url ? { backgroundImage: `url(${item.url})` } : {}}
								onClick={() => {
									if (item.url) {
										if (item.id === activeId) {
											handleSelect("0");
										} else {
											handleSelect({ id: item.id, url: item.url });
										}
									}
								}}
							></button>

							{avatars.some((av) => av.id === item.id) ? (
								""
							) : (
								<>
									<DeleteOutlineRounded
										className="delete-btn text-xl cursor-pointer -top-2 -right-2 absolute text-white bg-status-error rounded px-1 py-0.5 flex justify-center items-center"
										sx={{ fontSize: 30, color: "#fff" }}
										onClick={(e) => {
											e.stopPropagation();
											if (item.url) {
												removeAvatarFile?.(item.url);
												setUploadedAvatars((oldAvatars) =>
													oldAvatars.filter((av) => av.url !== item.url)
												);
											}
										}}
									/>
								</>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};
